import { Input, Menu, MenuItem, Scrollable } from '@pique/component';
import {
  adaptSerializedAttendees,
  AttendeeAvatars,
} from '@pique/core/components/pique/page-properties/attendee-avatars';
import { AttendeesService } from '@pique/core/modules/properties';
import type { SerializedAttendee } from '@pique/core/modules/properties/services/schema';
import { deduplicateAttendees } from '@pique/core/modules/properties/services/utils';
import { useLiveData, useService } from '@toeverything/infra';
import type { MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './multi-select.css';

export const AttendeeMultiSelect = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const attendeesService = useService(AttendeesService);
  const allAttendeesMap = useLiveData(attendeesService.attendees$);

  const allAttendees = useMemo(() => {
    const all: SerializedAttendee[] = [];
    for (const pageAttendees of Object.values(allAttendeesMap) as SerializedAttendee[][]) {
      all.push(...pageAttendees);
    }
    return deduplicateAttendees(all).sort((a, b) =>
      (a.name || a.email).localeCompare(b.name || b.email)
    );
  }, [allAttendeesMap]);

  const filteredAttendees = useMemo(() => {
    if (!search.trim()) return allAttendees;
    const s = search.toLowerCase();
    return allAttendees.filter(
      a =>
        a.name?.toLowerCase().includes(s) ||
        a.email?.toLowerCase().includes(s)
    );
  }, [allAttendees, search]);

  const selectedAttendees = useMemo(
    () => allAttendees.filter(a => value.includes(a.id)),
    [allAttendees, value]
  );

  const handleToggle = useCallback(
    (attendee: SerializedAttendee, e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const isSelected = value.includes(attendee.id);
      if (isSelected) {
        onChange(value.filter(v => v !== attendee.id));
      } else {
        onChange([...value, attendee.id]);
      }
    },
    [onChange, value]
  );

  const displayText = selectedAttendees.length
    ? selectedAttendees.map(a => a.name || a.email.split('@')[0]).join(', ')
    : 'Select attendees';

  const items = (
    <div style={{ width: 280 }}>
      <div style={{ padding: '8px 12px' }}>
        <Input
          placeholder="Search attendees..."
          value={search}
          onChange={setSearch}
          style={{ width: '100%' }}
        />
      </div>
      <Scrollable.Root>
        <Scrollable.Viewport className={styles.optionList}>
          {filteredAttendees.length === 0 ? (
            <MenuItem disabled>No attendees found</MenuItem>
          ) : (
            filteredAttendees.map(attendee => {
              const isSelected = value.includes(attendee.id);
              return (
                <MenuItem
                  key={attendee.id}
                  checked={isSelected}
                  onClick={(e: MouseEvent) => handleToggle(attendee, e)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <AttendeeAvatars
                    attendees={adaptSerializedAttendees([attendee])}
                    maxDisplay={1}
                    size="small"
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {attendee.name || attendee.email.split('@')[0]}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.6 }}>
                      {attendee.email}
                    </div>
                  </div>
                </MenuItem>
              );
            })
          )}
        </Scrollable.Viewport>
        <Scrollable.Scrollbar className={styles.scrollbar} />
      </Scrollable.Root>
    </div>
  );

  return (
    <Menu items={items}>
      <div className={styles.content} style={{ cursor: 'pointer' }}>
        {selectedAttendees.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AttendeeAvatars
              attendees={adaptSerializedAttendees(selectedAttendees)}
              maxDisplay={3}
              size="small"
            />
            <span className={styles.text}>{displayText}</span>
          </div>
        ) : (
          <span className={styles.text} style={{ opacity: 0.5 }}>
            {displayText}
          </span>
        )}
      </div>
    </Menu>
  );
};
